import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Heart, ShoppingCart, Filter, Package, Info } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Dialog, DialogTrigger, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useNavigate } from "react-router-dom";

interface Product {
  id: string;
  name: string;
  price: number;
  category: string;
  image_url: string | null;
  description: string | null;
  is_active: boolean;
  store: {
    id: string;
    name: string;
  } | null;
}

const ProductCatalog = () => {
  const [products, setProducts] = useState<Product[]>([]);
  const [infoProduct, setInfoProduct] = useState<Product | null>(null);
  const [selectedCategory, setSelectedCategory] = useState("semua");
  const [favorites, setFavorites] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();
  const navigate = useNavigate();

  const categories = [
    { id: "semua", name: "Semua" },
    { id: "makanan", name: "Makanan" },
    { id: "minuman", name: "Minuman" },
    { id: "merchandise", name: "Merchandise" },
    { id: "kerajinan", name: "Kerajinan" },
  ];

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('products')
        .select(`
          *,
          store:stores(id, name)
        `)
        .eq('is_active', true)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setProducts(data || []);
    } catch (error) {
      console.error('Error fetching products:', error);
      toast({
        title: "Error",
        description: "Gagal memuat produk. Silakan coba lagi.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const toggleFavorite = (productId: string) => {
    setFavorites(prev =>
      prev.includes(productId)
        ? prev.filter(id => id !== productId)
        : [...prev, productId]
    );
  };

  const handleBuyProduct = (product: Product) => {
    // toast({
    //   title: "Produk ditambahkan!",
    //   description: `${product.name} telah ditambahkan ke keranjang`,
    // });
    navigate(`/buy/${product.id}`);
  };

  const filteredProducts = products.filter(product =>
    selectedCategory === "semua" || product.category === selectedCategory
  );

  return (
    <section id="catalog" className="py-20 bg-gradient-card">
      <div className="container mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-5xl font-bold font-display text-foreground mb-4">
            <span className="text-primary">Katalog</span> Produk Kemerdekaan
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Pilihan terbaik produk lokal untuk merayakan HUT Kemerdekaan RI ke-80
          </p>
        </div>

        {/* Filter Kategori */}
        <div className="flex flex-wrap gap-2 mb-8 justify-center">
          <Button
            variant={selectedCategory === "semua" ? "independence" : "outline"}
            size="sm"
            onClick={() => setSelectedCategory("semua")}
            className="flex items-center space-x-1"
          >
            <Filter className="w-4 h-4" />
            <span>Semua</span>
          </Button>
          {categories.filter(cat => cat.id !== "semua").map((category) => (
            <Button
              key={category.id}
              variant={selectedCategory === category.id ? "independence" : "outline"}
              size="sm"
              onClick={() => setSelectedCategory(category.id)}
            >
              {category.name}
            </Button>
          ))}
        </div>

        {/* Loading State */}
        {loading && (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-muted-foreground">Memuat produk...</p>
          </div>
        )}

        {/* Empty State */}
        {!loading && products.length === 0 && (
          <div className="text-center py-12">
            <Package className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-foreground mb-2">Belum Ada Produk</h3>
            <p className="text-muted-foreground">
              Produk akan tampil di sini setelah admin menambahkannya
            </p>
          </div>
        )}

        {/* Grid Produk */}
        {!loading && filteredProducts.length > 0 && (
          <div className="grid grid-cols-2 gap-3 sm:gap-4 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
            {filteredProducts.map((product) => (
              <Card key={product.id} className="h-full flex flex-col group overflow-hidden hover:shadow-independence transition-all duration-300 transform hover:scale-105 bg-gradient-card border-none">
                <div className="relative aspect-square overflow-hidden">
                  <img
                    src={product.image_url || '/placeholder.svg'}
                    alt={product.name}
                    className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
                    onError={(e) => {
                      e.currentTarget.src = '/placeholder.svg';
                    }}
                  />
                  {/* Icon Favorite */}
                  {/* <Button
                    variant="ghost"
                    size="icon"
                    className={`absolute top-3 right-3 bg-background/80 hover:bg-background transition-all ${favorites.includes(product.id)
                      ? "text-red-500 hover:text-red-600"
                      : "text-muted-foreground hover:text-foreground"
                      }`}
                    onClick={() => toggleFavorite(product.id)}
                  >
                    <Heart
                      className={`w-5 h-5 ${favorites.includes(product.id) ? "fill-current" : ""}`}
                    />
                  </Button> */}
                  {/* Icon Info */}
                  <Dialog open={infoProduct?.id === product.id} onOpenChange={(open) => setInfoProduct(open ? product : null)}>
                    <DialogTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="absolute top-3 left-3 bg-background/80 hover:bg-background text-primary"
                        onClick={(e) => {
                          e.stopPropagation();
                          setInfoProduct(product);
                        }}
                      >
                        <Info className="w-5 h-5" />
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-md mx-auto">
                      <DialogHeader>
                        <DialogTitle className="text-xl font-bold text-foreground">{product.name}</DialogTitle>
                      </DialogHeader>
                      <div className="space-y-4">
                        {/* Product Image */}
                        <div className="relative aspect-square rounded-lg overflow-hidden bg-muted">
                          <img
                            src={product.image_url || '/placeholder.svg'}
                            alt={product.name}
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              e.currentTarget.src = '/placeholder.svg';
                            }}
                          />
                        </div>

                        {/* Product Info */}
                        <div className="space-y-3">
                          <div className="flex items-center justify-between">
                            <Badge className="bg-gradient-independence text-primary-foreground capitalize">
                              {product.category}
                            </Badge>
                            <span className="text-2xl font-bold text-primary">
                              Rp {product.price.toLocaleString('id-ID')}
                            </span>
                          </div>

                          {product.store?.name && (
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-medium text-muted-foreground">Toko:</span>
                              <span className="text-sm font-medium text-primary">{product.store.name}</span>
                            </div>
                          )}

                          {product.description && (
                            <div>
                              <h4 className="text-sm font-medium text-foreground mb-2">Deskripsi:</h4>
                              <p className="text-sm text-muted-foreground leading-relaxed whitespace-pre-wrap">
                                {product.description}
                              </p>
                            </div>
                          )}

                          {/* <Button
                            variant="independence"
                            className="w-full mt-4"
                            onClick={() => {
                              setInfoProduct(null);
                              handleBuyProduct(product);
                            }}
                          >
                            <ShoppingCart className="w-4 h-4 mr-2" />
                            Beli Sekarang
                          </Button> */}
                        </div>
                      </div>
                    </DialogContent>
                  </Dialog>
                  <Badge className="absolute bottom-3 left-3 bg-gradient-independence text-primary-foreground capitalize">
                    {product.category}
                  </Badge>
                </div>

                <CardContent className="p-4 flex flex-col flex-1 justify-between">
                  <div>
                    <h3 className="font-bold text-base text-foreground mb-1 line-clamp-2">
                      {product.name}
                    </h3>
                    <p
                      className="text-primary text-xs font-medium mb-2 cursor-pointer hover:underline"
                      onClick={() => {
                      if (product.store?.id) {
                        navigate(`/test-toko/${product.store.id}`);
                      }
                      }}
                    >
                      {product.store?.name || "Toko tidak tersedia"}
                    </p>
                    <p className="text-muted-foreground text-sm mb-4 line-clamp-2">
                      {product.description || "Tidak ada deskripsi"}
                    </p>
                  </div>
                  <div className="mt-4 flex flex-col gap-2">
                    <span className="text-lg font-bold text-primary block break-words text-left">
                      Rp {product.price.toLocaleString('id-ID')}
                    </span>
                    <Button
                      variant="independence"
                      size="sm"
                      className="w-full hover:shadow-golden"
                      onClick={() => handleBuyProduct(product)}
                    >
                      <ShoppingCart className="w-4 h-4 mr-1" />
                      Beli
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* No Results for Filter */}
        {!loading && products.length > 0 && filteredProducts.length === 0 && (
          <div className="text-center py-12">
            <Package className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-foreground mb-2">Tidak Ada Produk</h3>
            <p className="text-muted-foreground">
              Tidak ada produk dalam kategori "{selectedCategory}"
            </p>
          </div>
        )}
      </div>
    </section>
  );
};

export default ProductCatalog;